import { createContext, useContext, useEffect, useState } from "react";
import "./App.css";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";

import secureLocalStorage from "react-secure-storage";

import AuditForms from "./components/qms/auditForms/auditForms";

//Components
import NotFound from "./components/layouts/NotFound";
import UnderMaintenance from "./components/layouts/UnderMaintenance";
import Main from "./components/layouts/main/main";
import Survey from "./components/voc/survey/survey";
import { GlobalStateContext } from "./utils/GlobalStateContext";
import APICaller from "./utils/APICaller";
import { message } from "antd";
import CryptoJS from "crypto-js";
import { storeAccessToken } from "./utils/tokenStorage";
import LogoutHandler from "./components/logoutHandler/logoutHandler";

import axios from "axios";
import { componentMap, roles } from "./routes";

const UserContext = createContext();

function App() {
  const [isLoggedin, setIsLoggedin] = useState(false);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname?.split("/");
  const navigateHome = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    const accessToken = secureLocalStorage.getItem("accessToken");

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token"); // Get the token from URL

    if (token) {
      validateToken(token);
    } else {
      const logoutReq = pathName[1] === "logout" ? true : false;
      if (!accessToken && !logoutReq) {
        window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
      }
      if (location.pathname === "/" && accessToken) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, []);

  const deriveKey = (password, salt) => {
    return CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: 65536,
    });
  };

  // Function to decrypt the data
  const decrypt = (encryptedData, encryptionKey) => {
    try {
      // Decode the base64 encrypted data into a byte array
      const combined = CryptoJS.enc.Base64.parse(encryptedData);

      // Extract salt (first 16 bytes)
      const salt = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));

      // Extract IV (next 16 bytes)
      const iv = CryptoJS.lib.WordArray.create(combined.words.slice(4, 8));

      // The remaining part is the encrypted content
      const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(8));

      // Derive the secret key using PBKDF2 with the provided salt
      const secretKey = deriveKey(encryptionKey, salt);

      // Decrypt the data using AES with the derived key and IV
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: encrypted },
        secretKey,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7, // Ensure proper padding
        }
      );

      // Return the decrypted data as a UTF-8 string
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      // Check if decryption is successful
      if (!decryptedText) {
        throw new Error("Decrypted data is empty or invalid UTF-8.");
      }

      return decryptedText;
    } catch (error) {
      console.error("Decryption error:", error.message);
      return null;
    }
  };

  //API to validate token
  const validateToken = async (token) => {
    let deviceInfo = "";
    try {
      //Saving user device info

      const response = await axios.get(process.env.REACT_APP_IPIFY);

      if (response) {
        const userAgent = navigator.userAgent; // Accessing navigator
        const screenWidth = window.innerWidth; // Screen width
        const screenHeight = window.innerHeight; // Screen height
        const ip = response.data.ip;
        deviceInfo = {
          userAgent,
          screenWidth,
          screenHeight,
          ip,
        };
      }
    } catch (error) {
      console.error("Error fetching IP:", error);
    }
    try {
      const endpoint = "/validateToken";
      const method = "POST";
      const payload = {
        auth: token,
        device: deviceInfo,
      };
      await APICaller("", token, endpoint, method, payload)
        .then((response) => {
          if (response.statusCode === 200) {
            // Decrypt the encrypted response data
            const decryptedData = decrypt(
              response.respnse,
              process.env.REACT_APP_SSO_ENCRYPTION_KEY
            );

            const decryptedObject = JSON.parse(decryptedData);
            const getAccessTokenExpiresOn = decryptedObject.expiryTime;

            secureLocalStorage.setItem("lIU", decryptedObject?.user.name);
            secureLocalStorage.setItem(
              "accessToken",
              decryptedObject?.accessToken
            );

            secureLocalStorage.setItem(
              "accessToken",
              decryptedObject?.accessToken
            );

            secureLocalStorage.setItem(
              "logoutSuccessUrl",
              decryptedObject?.decryptedData?.logoutSuccessUrl
            );

            // Handle successful login here
            setGlobalState((prevGlobalState) => ({
              ...prevGlobalState,
              isLoggedIn: true,
            }));

            storeAccessToken(
              decryptedObject?.accessToken,
              getAccessTokenExpiresOn
            );
            secureLocalStorage.setItem("lIUId", decryptedObject?.user?._id);
            secureLocalStorage.setItem("role", decryptedObject?.user?.role);
            secureLocalStorage.setItem("empID", decryptedObject?.user?.empID);

            secureLocalStorage.setItem(
              "reportingSupervisor",
              decryptedObject?.user?.reportingSupervisor
            );
            response.statusCode === 200 && navigateHome();
          }
        })
        .catch((error) => {});
    } catch (error) {
      showNotification(
        "The email you used does not exist in our system. Please logout and try logging in with a different account."
      );

      // Trigger the MSAL login popup again to allow the user to log in with a different email
      clearAndLogout();
    }
  };

  //To show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  const clearAndLogout = async () => {
    secureLocalStorage.clear();
    localStorage.setItem("logout", Date.now()); // Triggers storage event
    window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
  };

  useEffect(() => {
    if (
      (location.pathname.split("/")[1] === "auditForms" ||
        location.pathname.split("/")[1] === "audits" ||
        location.pathname.split("/")[1] === "escalations") &&
      location.pathname.split("/")[2]
    ) {
      // Get elements by class name
      let sideNavElements = document.getElementsByClassName("sidenav");
      let rightSideElements = document.getElementsByClassName("right-side");

      // Check if there are elements with the specified class
      if (sideNavElements.length > 0) {
        // Access the first element in the collection and toggle the class
        sideNavElements[0].classList.remove("open");
      }

      if (rightSideElements.length > 0) {
        // Access the first element in the collection and toggle the class
        rightSideElements[0].classList.remove("right-side-open");
      }
    }
  });

  // Define your components - Forbidden, AuditForms, Audits, AuditView, Users, AddUser, Roles, AddRole
  const userRole = secureLocalStorage.getItem("role");

  const hasAccess = (role) => {
    if (window.performance) {
      const entries = performance.getEntriesByType("navigation");

      if (entries.length > 0 && entries[0].type === "reload") {
        const searchKeywordOnRefresh = document.querySelector(".inputS");
        if (
          searchKeywordOnRefresh?.value === "" ||
          searchKeywordOnRefresh?.value === undefined
        ) {
          if (secureLocalStorage.getItem("hasSearchedValue")) {
            secureLocalStorage.removeItem("hasSearchedValue");
          }
          if (secureLocalStorage.getItem("searchKeyword")) {
            secureLocalStorage.removeItem("searchKeyword");
          }
        }

        if (
          (location.pathname.split("/")[1] !== "users" ||
            location.pathname.split("/")[1] !== "roles") &&
          location.pathname.split("/")[2]
        ) {
          // Get elements by class name
          let sideNavElements = document.getElementsByClassName("sidenav");
          let rightSideElements = document.getElementsByClassName("right-side");

          // Check if there are elements with the specified class
          if (sideNavElements.length > 0) {
            // Access the first element in the collection and toggle the class
            sideNavElements[0].classList.remove("open");
          }

          if (rightSideElements.length > 0) {
            // Access the first element in the collection and toggle the class
            rightSideElements[0].classList.remove("right-side-open");
          }
        }
      }
    }

    return roles[role] || [];
  };

  const accessibleRoutes = userRole
    ? hasAccess(userRole)
    : ["/underMaintenance"];

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const accessToken = secureLocalStorage.getItem("accessToken");

    if (userRole === null && accessToken) {
      setGlobalState(false);
      secureLocalStorage.clear();
      navigate("/dashboard");
    }
    setLoading(false); // Set loading to false after processing
  }, []); // Empty dependency array ensures this effect runs only once on component mount

  // Render loading indicator if still loading
  if (loading) {
    return <div></div>;
  }

  return (
    <UserContext.Provider
      value={{
        loggedIn: isLoggedin,
        loginSetter: () => {
          setIsLoggedin(!isLoggedin);
        },
      }}
    >
      <Routes>
        <Route
          index
          path="/"
          element={
            isLoggedin && (
              <Main
                children={<AuditForms />}
                key_elem={"1"}
                nameLink="Audit Forms"
              />
            )
          }
        />
        <Route path="/underMaintenance" element={<UnderMaintenance />} />
        <Route path="/survey/:id" element={<Survey />} />
        {accessibleRoutes.map((route) => (
          <Route key={route} path={route} element={componentMap[route]} />
        ))}
        <Route path="/logout/:empID" element={<LogoutHandler />} />
        <Route
          path="*"
          element={<Navigate to={componentMap.noRoute} replace />}
        />{" "}
        {/* Handling default or unmatched routes */}
        <Route path="/404" element={<NotFound />} />
      </Routes>
    </UserContext.Provider>
  );
}

export default App;
export { UserContext };
